import { urlUtil } from '@grafana/data';
import { locationService } from '@grafana/runtime';
import { getTimeSrv } from 'app/features/dashboard/services/TimeSrv';
import { type DashboardModel } from 'app/features/dashboard/state/DashboardModel';
import { DashboardScene } from 'app/features/dashboard-scene/scene/DashboardScene';
import { getVariablesUrlParams } from 'app/features/variables/getAllVariableValuesForUrl';

import { type ReportDashboard, type StepKey } from '../../types';
import { getRange } from '../../utils/time';
import { type SelectDashboardScene } from '../ReportFormV2/sections/SelectDashboards/SelectDashboardScene';
import { REPORT_BASE_URL, NEW_REPORT_URL } from '../constants';
import { defaultTimeRange } from '../state/reducers';

import { getTemplateVariables } from './dashboards';
import { collectVariables } from './variables';

export const getUrlValues = () => {
  const search = locationService.getSearch();
  if (search.size === 0) {
    return null;
  }
  const urlParams = new URLSearchParams(search);

  return {
    timeRange: {
      to: urlParams.get('to') || '',
      from: urlParams.get('from') || '',
    },
    dashboard: {
      uid: urlParams.get('db-uid') || '',
      name: urlParams.get('db-name') || '',
    },
    variables: collectVariables(),
  };
};

export const getTimeRangeFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const to = urlParams.get('to');
  const from = urlParams.get('from');
  if (from && to) {
    return { from, to };
  }
  return null;
};

export const getSectionUrl = (section: StepKey, id?: string | number) => {
  return `${REPORT_BASE_URL}/${section}${id ? '/' + id : ''}`;
};

export const getStepUrl = (id?: string | number) => (step: StepKey) => {
  return `${REPORT_BASE_URL}/${step}${id ? '/' + id : ''}`;
};

export const getNewReportUrl = (dashboard: DashboardModel | DashboardScene) => {
  const variablesQuery = urlUtil.toUrlParams(getVariablesUrlParams());
  const timeRangeUrl = urlUtil.toUrlParams(getTimeSrv().timeRangeForUrl());

  const uid = dashboard instanceof DashboardScene ? dashboard?.state.uid : dashboard?.uid;
  const title = dashboard instanceof DashboardScene ? dashboard?.state.title : dashboard?.title;

  return `${NEW_REPORT_URL}/?${variablesQuery}&${timeRangeUrl}&db-uid=${uid}&db-name=${title}`;
};

export const getReportDashboardsAsUrlParam = (reportDashboards: ReportDashboard[]) => {
  return JSON.stringify(
    reportDashboards.map((db) => {
      const { from, to } = getRange(db.timeRange).raw || defaultTimeRange.raw;
      return {
        dashboard: { uid: db.dashboard?.uid },
        timeRange: { from: from.valueOf().toString(), to: to.valueOf().toString() },
        reportVariables: db.reportVariables,
      };
    })
  );
};

export const getReportDashboardsAsUrlParamV2 = (dashboards: SelectDashboardScene[]) => {
  return JSON.stringify(
    dashboards.map((db) => {
      const { state } = db;
      const { from, to } = getRange(state.$timeRange?.state.value).raw || defaultTimeRange.raw;
      return {
        dashboard: { uid: state.uid },
        timeRange: { from: from.valueOf().toString(), to: to.valueOf().toString() },
        reportVariables: getTemplateVariables(state.$variables),
      };
    })
  );
};
